(() => {
  const DISCOUNT_FLAG_PARAM = 'ps_discount';
  const PRIVATE_SALE_QUERY_PARAM = 'ps';

  function buildUrlWithParams(base, params) {
    const url = new URL(base, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });
    return `${url.pathname}${url.search}${url.hash}`;
  }

  async function sha256(value) {
    const source = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', source);
    return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function unlockGate(root) {
    root.hidden = true;
    document.body.classList.remove('private-sale-locked');
    document.body.classList.add('private-sale-unlocked');
  }

  function lockGate(root) {
    root.hidden = false;
    document.body.classList.add('private-sale-locked');
    document.body.classList.remove('private-sale-unlocked');
  }

  function maybeActivateDiscount(root) {
    const discountCode = root.dataset.discountCode;
    const discountKey = root.dataset.discountKey;
    const activateDiscount = root.dataset.activateDiscount === 'true';
    if (!activateDiscount || !discountCode || !discountKey) return false;

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get(DISCOUNT_FLAG_PARAM) === '1') {
      sessionStorage.setItem(discountKey, '1');
      currentUrl.searchParams.delete(DISCOUNT_FLAG_PARAM);
      window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      return false;
    }

    if (sessionStorage.getItem(discountKey) === '1') return false;

    const redirectPath = buildUrlWithParams(root.dataset.redirectPath || window.location.pathname, {
      [DISCOUNT_FLAG_PARAM]: '1'
    });
    window.location.assign(`/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(redirectPath)}`);
    return true;
  }

  async function setupGate(root) {
    const accessHash = (root.dataset.accessHash || '').trim().toLowerCase();
    const storageKey = root.dataset.storageKey;
    const input = root.querySelector('[data-private-sale-input]');
    const submit = root.querySelector('[data-private-sale-submit]');
    const error = root.querySelector('[data-private-sale-error]');
    const currentUrl = new URL(window.location.href);
    const isProductGate = root.dataset.productPrivateSale === 'true';
    const isPrivateSaleView = currentUrl.searchParams.get(PRIVATE_SALE_QUERY_PARAM) === '1';

    if (isProductGate && !isPrivateSaleView) {
      root.hidden = true;
      document.body.classList.remove('private-sale-locked');
      document.body.classList.remove('private-sale-unlocked');
      return;
    }

    if (!accessHash || !storageKey || !input || !submit) {
      lockGate(root);
      return;
    }

    const storedValue = localStorage.getItem(storageKey);
    if (storedValue === accessHash) {
      unlockGate(root);
      maybeActivateDiscount(root);
      return;
    }

    lockGate(root);

    async function attemptUnlock() {
      const enteredValue = input.value.trim();
      if (!enteredValue) return;

      submit.disabled = true;
      if (error) error.hidden = true;

      try {
        const hash = await sha256(enteredValue);
        if (hash === accessHash) {
          localStorage.setItem(storageKey, accessHash);
          unlockGate(root);
          if (!maybeActivateDiscount(root)) {
            input.value = '';
          }
          return;
        }
      } catch (err) {
        console.error(err);
      }

      if (error) error.hidden = false;
      submit.disabled = false;
    }

    submit.addEventListener('click', attemptUnlock);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        attemptUnlock();
      }
    });
  }

  function applyPrivateSaleQueryToProductUrls() {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get(PRIVATE_SALE_QUERY_PARAM) !== '1') return;

    document.querySelectorAll('[data-url^="/products/"]').forEach((node) => {
      const nextUrl = buildUrlWithParams(node.dataset.url, { [PRIVATE_SALE_QUERY_PARAM]: '1' });
      node.dataset.url = nextUrl;
    });
  }

  function buildPrivateSalePriceMarkup(variant) {
    if (!variant || !variant.privateSalePrice || variant.privateSalePrice >= variant.price) return null;

    return `
      <div class="price private-sale-price price--large${variant.available === false ? ' price--sold-out' : ''}">
        <div class="price__container">
          <div class="private-sale-price__stack">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="private-sale-price__regular">
              <s class="price-item price-item--regular">${variant.moneyPrice}</s>
            </span>
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="private-sale-price__sale price-item price-item--sale">${variant.moneyPrivateSalePrice}</span>
          </div>
        </div>
      </div>
    `;
  }

  function setupPrivateSaleProduct() {
    const payloadNode = document.querySelector('[data-private-sale-product]');
    if (!payloadNode) return;

    let payload;
    try {
      payload = JSON.parse(payloadNode.textContent);
    } catch (error) {
      console.error(error);
      return;
    }

    const currentUrl = new URL(window.location.href);
    const isPrivateSaleView = currentUrl.searchParams.get(PRIVATE_SALE_QUERY_PARAM) === '1';
    if (!isPrivateSaleView) return;

    applyPrivateSaleQueryToProductUrls();

    const gateRoot = document.querySelector('[data-private-sale-gate]');
    const storageKey = gateRoot?.dataset.storageKey;
    const accessHash = gateRoot?.dataset.accessHash;
    const isUnlocked = storageKey && accessHash && localStorage.getItem(storageKey) === accessHash;
    if (gateRoot && !isUnlocked) {
      gateRoot.hidden = false;
      document.body.classList.add('private-sale-locked');
    }

    const variantMap = new Map((payload.variants || []).map((variant) => [String(variant.id), variant]));

    const variantRadios = document.querySelector('variant-radios');
    const sectionId = variantRadios?.dataset.section;

    function getCurrentVariantId() {
      if (sectionId) {
        const scopedInput = document.querySelector(`#product-form-${sectionId} [name="id"]`);
        if (scopedInput?.value) return String(scopedInput.value);
      }

      const variantInput = document.querySelector('product-form [name="id"]');
      return variantInput?.value ? String(variantInput.value) : null;
    }

    function renderVariantPrice(variantId = getCurrentVariantId()) {
      const variant = variantMap.get(String(variantId));
      const markup = buildPrivateSalePriceMarkup(variant);
      if (!markup) return;

      document.querySelectorAll('[id^="price-"]').forEach((priceBlock) => {
        if (priceBlock.querySelector('.private-sale-price')) return;
        priceBlock.innerHTML = `<div>${markup}</div>`;
        priceBlock.classList.remove('visibility-hidden');
      });
    }

    function scheduleRender(variantId) {
      window.requestAnimationFrame(() => {
        renderVariantPrice(variantId);
        setTimeout(() => renderVariantPrice(variantId), 50);
      });
    }

    renderVariantPrice();

    document.querySelectorAll('[id^="price-"]').forEach((priceBlock) => {
      const observer = new MutationObserver(() => {
        if (!priceBlock.querySelector('.private-sale-price')) {
          renderVariantPrice();
        }
      });

      observer.observe(priceBlock, {
        childList: true,
        subtree: true
      });
    });

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        applyPrivateSaleQueryToProductUrls();
        scheduleRender(event?.data?.variant?.id);
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-private-sale-gate]').forEach((root) => {
      setupGate(root);
    });
    setupPrivateSaleProduct();
  });
})();
